import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  TextField,
  Stack,
  IconButton,
  Link,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthGen2 } from '../hooks/useAuthGen2';
import FriendsManager from '../utils/FriendsManager';
import { fetchAuthSession, signIn, signInWithRedirect, signUp, confirmSignUp } from 'aws-amplify/auth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import GoogleIcon from '@mui/icons-material/Google';
import { Facebook } from '@mui/icons-material';

const AcceptInvite: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading, refreshAuth } = useAuthGen2();

  // Friend invitation state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [friendName, setFriendName] = useState<string>('');

  // Auth form state
  const [tabValue, setTabValue] = useState(0);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [isAwaitingConfirmation, setIsAwaitingConfirmation] = useState(false);
  const [signInLoading, setSignInLoading] = useState(false);
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const inviteCode = searchParams.get('code');

  useEffect(() => {
    // Wait for auth to load
    if (authLoading) {
      return;
    }

    // If not authenticated, do nothing (the page will show login prompt)
    if (!isAuthenticated) {
      return;
    }

    // If no invite code, show error
    if (!inviteCode) {
      setError('No invitation code provided');
      return;
    }

    // Accept the invitation
    acceptInvitation();
  }, [authLoading, isAuthenticated, inviteCode]);

  const acceptInvitation = async () => {
    if (!inviteCode) {
      setError('No invitation code provided');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('No authentication token found');
      }

      const result = await FriendsManager.acceptInvitation(inviteCode, token);

      if (result.success) {
        setSuccess(true);
        setFriendName(result.friendId);

        // Redirect to friends page after 2 seconds
        setTimeout(() => {
          navigate('/friends');
        }, 2000);
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleProviderSignIn = async (provider: 'Google' | 'Facebook') => {
    try {
      setAuthError('');
      if (isAuthenticated && !authLoading) {
        return;
      }
      await signInWithRedirect({ provider });
    } catch (error) {
      console.error('OAuth sign in error:', error);
      if (error && typeof error === 'object' && 'name' in error) {
        if ((error as any).name === 'UserAlreadyAuthenticatedException') {
          await refreshAuth();
        }
      }
    }
  };

  const handleSignIn = async () => {
    try {
      setAuthError('');
      if (!username || !password) {
        setAuthError('Please enter both email and password');
        return;
      }

      if (isAuthenticated && !authLoading) {
        return;
      }

      setSignInLoading(true);

      await signIn({ username, password });
      await refreshAuth();
      setSignInLoading(false);
    } catch (error) {
      console.error('Email sign in error:', error);
      setSignInLoading(false);

      if (error && typeof error === 'object' && 'name' in error) {
        if ((error as any).name === 'UserAlreadyAuthenticatedException') {
          await refreshAuth();
        } else if ((error as any).name === 'NotAuthorizedException') {
          setAuthError('Incorrect email or password');
        } else if ((error as any).name === 'UserNotFoundException') {
          setAuthError('No account found with this email');
        } else if ((error as any).message) {
          setAuthError((error as any).message);
        } else {
          setAuthError('Sign in failed. Please try again.');
        }
      } else {
        setAuthError('Sign in failed. Please try again.');
      }
    }
  };

  const handleSignUp = async () => {
    try {
      setAuthError('');
      if (!signUpEmail || !signUpPassword) {
        setAuthError('Please enter both email and password');
        return;
      }

      if (isAuthenticated && !authLoading) {
        return;
      }

      setSignUpLoading(true);

      await signUp({
        username: signUpEmail,
        password: signUpPassword,
        options: {
          userAttributes: {
            email: signUpEmail
          }
        }
      });
      setSignUpLoading(false);
      setIsAwaitingConfirmation(true);
    } catch (error) {
      console.error('Email sign up error:', error);
      setSignUpLoading(false);

      if (error && typeof error === 'object' && 'name' in error) {
        if ((error as any).name === 'UsernameExistsException') {
          setAuthError('An account with this email already exists');
        } else if ((error as any).name === 'InvalidPasswordException') {
          setAuthError('Password must be 8+ characters with uppercase, lowercase, number, and symbol');
        } else if ((error as any).message) {
          setAuthError((error as any).message);
        } else {
          setAuthError('Sign up failed. Please try again.');
        }
      } else {
        setAuthError('Sign up failed. Please try again.');
      }
    }
  };

  const handleConfirmSignUp = async () => {
    try {
      setAuthError('');
      if (!confirmationCode) {
        setAuthError('Please enter the confirmation code');
        return;
      }

      setSignUpLoading(true);

      await confirmSignUp({
        username: signUpEmail,
        confirmationCode: confirmationCode
      });
      setIsAwaitingConfirmation(false);

      // Automatically sign in after confirmation
      await signIn({
        username: signUpEmail,
        password: signUpPassword
      });

      await refreshAuth();
      setSignUpLoading(false);
    } catch (error) {
      console.error('Confirmation error:', error);
      setSignUpLoading(false);

      if (error && typeof error === 'object' && 'name' in error) {
        if ((error as any).name === 'CodeMismatchException') {
          setAuthError('Invalid confirmation code. Please check and try again.');
        } else if ((error as any).name === 'ExpiredCodeException') {
          setAuthError('Confirmation code has expired. Please request a new one.');
        } else if ((error as any).message) {
          setAuthError((error as any).message);
        } else {
          setAuthError('Confirmation failed. Please try again.');
        }
      } else {
        setAuthError('Confirmation failed. Please try again.');
      }
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setAuthError('');
  };

  // Sign In View Component
  const SignInView = () => {
    return (
      <Box sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
        {authError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAuthError('')}>
            {authError}
          </Alert>
        )}
        <Stack justifyContent="center" alignItems="center" direction="row" spacing={4}>
          <IconButton
            onClick={() => handleProviderSignIn("Facebook")}
            disabled={signInLoading}
          >
            <Facebook fontSize='large' />
          </IconButton>
          <IconButton
            onClick={() => handleProviderSignIn("Google")}
            disabled={signInLoading}
          >
            <GoogleIcon fontSize='large' />
          </IconButton>
        </Stack>
        <TextField
          id="email"
          label="Email"
          variant="outlined"
          onChange={(e) => setUsername(e.target.value)}
          fullWidth
          sx={{ mt: 2 }}
          disabled={signInLoading}
          inputProps={{
            autoComplete: 'email'
          }}
        />
        <TextField
          id="password"
          label="Password"
          type="password"
          variant="outlined"
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          sx={{ mt: 1 }}
          disabled={signInLoading}
          inputProps={{
            autoComplete: 'current-password'
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSignIn();
            }
          }}
        />
        <Button
          onClick={handleSignIn}
          variant="contained"
          sx={{ mt: 2 }}
          fullWidth
          disabled={signInLoading}
          startIcon={signInLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {signInLoading ? 'Signing In...' : 'Sign In'}
        </Button>
      </Box>
    );
  };

  // Sign Up View Component
  const SignUpView = () => {
    if (isAwaitingConfirmation) {
      return (
        <Box sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
          {authError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAuthError('')}>
              {authError}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            A confirmation code has been sent to your email. Please check your spam folder if you don't see it.
          </Alert>
          <Stack spacing={2}>
            <TextField
              id="confirmationCode"
              label="Confirmation Code"
              variant="outlined"
              onChange={(e) => setConfirmationCode(e.target.value)}
              fullWidth
              helperText="Check your email (and spam folder) for the confirmation code"
              disabled={signUpLoading}
            />
            <Button
              onClick={handleConfirmSignUp}
              variant="contained"
              disabled={signUpLoading}
              startIcon={signUpLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {signUpLoading ? 'Confirming...' : 'Confirm Sign Up'}
            </Button>
            <Button
              onClick={() => {
                setIsAwaitingConfirmation(false);
                setAuthError('');
              }}
              variant="text"
              size="small"
              disabled={signUpLoading}
            >
              Back to Sign Up
            </Button>
          </Stack>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3, width: '100%', boxSizing: 'border-box' }}>
        {authError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAuthError('')}>
            {authError}
          </Alert>
        )}
        <Stack justifyContent="center" alignItems="center" direction="row" spacing={4}>
          <IconButton
            onClick={() => handleProviderSignIn("Facebook")}
            disabled={signUpLoading}
          >
            <Facebook fontSize='large' />
          </IconButton>
          <IconButton
            onClick={() => handleProviderSignIn("Google")}
            disabled={signUpLoading}
          >
            <GoogleIcon fontSize='large' />
          </IconButton>
        </Stack>
        <TextField
          id="signupEmail"
          label="Email"
          variant="outlined"
          onChange={(e) => setSignUpEmail(e.target.value)}
          fullWidth
          sx={{ mt: 2 }}
          disabled={signUpLoading}
          inputProps={{
            autoComplete: 'email'
          }}
        />
        <TextField
          id="signupPassword"
          label="Password"
          type="password"
          variant="outlined"
          onChange={(e) => setSignUpPassword(e.target.value)}
          fullWidth
          helperText="Must be 8+ chars with uppercase, lowercase, number, and symbol"
          sx={{ mt: 1 }}
          disabled={signUpLoading}
          inputProps={{
            autoComplete: 'new-password'
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSignUp();
            }
          }}
        />
        <Button
          onClick={handleSignUp}
          variant="contained"
          sx={{ mt: 2 }}
          fullWidth
          disabled={signUpLoading}
          startIcon={signUpLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {signUpLoading ? 'Signing Up...' : 'Sign Up'}
        </Button>
      </Box>
    );
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4, textAlign: 'center', px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Container>
    );
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
        <Paper elevation={1} sx={{ width: '100%', maxWidth: '90vw' }}>
          <Typography variant="h5" sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
            Friend Invitation
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', px: 3, pb: 2 }}>
            Please log in or create an account to accept this friend invitation.
          </Typography>
          <Tabs sx={{ px: 1 }} value={tabValue} onChange={handleTabChange} variant="fullWidth">
            <Tab label="Sign In"/>
            <Tab label="Sign Up"/>
          </Tabs>
          {tabValue === 0 && SignInView()}
          {tabValue === 1 && SignUpView()}
        </Paper>
      </Container>
    );
  }

  // Show error if no invite code
  if (!inviteCode) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Invalid Invitation
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            This invitation link is invalid or has expired.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/friends')}
          >
            Go to Friends
          </Button>
        </Paper>
      </Container>
    );
  }

  // Show success message
  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Friend Added!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You are now friends! Redirecting to your friends list...
          </Typography>
          <CircularProgress size={24} />
        </Paper>
      </Container>
    );
  }

  // Show error message
  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Failed to Accept Invitation
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => acceptInvitation()}
            >
              Try Again
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/friends')}
            >
              Go to Friends
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Show loading state
  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4, textAlign: 'center', px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
      <Paper sx={{ p: 4 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Accepting Friend Invitation...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we process your invitation.
        </Typography>
      </Paper>
    </Container>
  );
};

export default AcceptInvite;
